import { useEffect, useState } from "react";
import { Envelope, Eye, EyeSlash, Key, MaskHappy, PaperPlaneTilt, ShieldCheck, Users, Wallet } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

export default function AdminSettingsPage() {
  const { billing, refreshBilling } = useAuth();
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [key, setKey] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState(null);

  // SMTP form state
  const [smtp, setSmtp] = useState({
    smtp_host: "", smtp_port: "587", smtp_username: "",
    smtp_password: "", smtp_from_email: "", smtp_from_name: "Auditor",
    smtp_use_tls: "true",
  });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpShow, setSmtpShow] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const [s, st] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/stats"),
      ]);
      setSettings(s.data);
      setStats(st.data);
      // Hydrate SMTP form with any non-secret values already saved
      setSmtp((cur) => ({
        ...cur,
        smtp_host: s.data.smtp_host || cur.smtp_host,
        smtp_port: s.data.smtp_port || cur.smtp_port,
        smtp_username: s.data.smtp_username || cur.smtp_username,
        smtp_from_email: s.data.smtp_from_email || cur.smtp_from_email,
        smtp_from_name: s.data.smtp_from_name || cur.smtp_from_name,
        smtp_use_tls: s.data.smtp_use_tls || cur.smtp_use_tls,
        smtp_password: "",
      }));
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load admin data");
      }
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/settings", { stripe_api_key: key });
      toast.success("Stripe key saved");
      setKey("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // Save SMTP. Send only the password if the user typed one — leaving
  // the field empty means "keep the existing one".
  const saveSmtp = async (e) => {
    e.preventDefault();
    setSmtpSaving(true);
    try {
      const payload = { ...smtp };
      if (!payload.smtp_password) delete payload.smtp_password;
      await api.post("/admin/settings", payload);
      toast.success("SMTP settings saved");
      setSmtp((cur) => ({ ...cur, smtp_password: "" }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save SMTP");
    } finally {
      setSmtpSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error("Enter a destination email first"); return; }
    setTesting(true);
    try {
      await api.post("/admin/settings/test-smtp", { to: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test email failed");
    } finally {
      setTesting(false);
    }
  };

  if (!settings) {
    return <AppShell><div className="text-ink-muted">Loading…</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="label-overline mb-2">Admin</div>
      <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-none mb-10">
        Settings
      </h1>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-12">
        <StatCard icon={Users} label="Users" value={stats?.users ?? "—"} testId="admin-stat-users" />
        <StatCard icon={ShieldCheck} label="Paid users" value={stats?.paid_users ?? "—"} testId="admin-stat-paid" />
        <StatCard icon={Wallet} label="Revenue" value={stats ? `$${stats.revenue_usd.toFixed(2)}` : "—"} testId="admin-stat-revenue" />
        <StatCard icon={Key} label="Audits run" value={stats?.audits ?? "—"} testId="admin-stat-audits" />
      </div>

      {/* View-as plan switcher — admin-only impersonation so you can
          preview the UI as Free / Pro / Agency without making another
          account. Actually changes your own plan in the DB. */}
      <div className="surface-card hard-shadow-sm p-6 lg:p-8 max-w-2xl mb-8" data-testid="view-as-card">
        <div className="flex items-center gap-2 mb-2">
          <MaskHappy size={20} className="text-brand" weight="duotone" />
          <div className="label-overline">Admin — preview as</div>
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight mb-1">View the app as another plan</h2>
        <p className="text-sm text-ink-muted mb-5">
          Flip your own plan to <span className="mono">free</span>, <span className="mono">pro</span>, or
          <span className="mono"> agency</span> to see exactly what each
          tier sees. Switch back any time — this only affects your account.
        </p>

        <div className="border border-border rounded-sm p-4 bg-secondary mb-4 text-sm">
          <div className="label-overline mb-1">Current plan</div>
          <div className="flex items-baseline gap-2">
            <span className="font-display text-xl font-black">{(billing?.plan_label || billing?.plan || "—").toUpperCase()}</span>
            {billing?.plan === "agency" && <span className="mono text-[10px] text-success">UNLIMITED</span>}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[
            { plan: "free",   label: "Free",   tag: "3 / 30d" },
            { plan: "pro",    label: "Pro",    tag: "10 / 30d" },
            { plan: "agency", label: "Agency", tag: "Unlimited" },
          ].map((opt) => {
            const active = billing?.plan === opt.plan;
            return (
              <button
                key={opt.plan}
                onClick={async () => {
                  if (active) return;
                  setSwitching(opt.plan);
                  try {
                    await api.post("/admin/view-as", { plan: opt.plan });
                    await refreshBilling();
                    toast.success(`Switched to ${opt.label}`);
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Failed");
                  } finally { setSwitching(null); }
                }}
                disabled={switching !== null}
                data-testid={`view-as-${opt.plan}`}
                className={`flex flex-col items-center gap-1 p-3 rounded-sm border-2 transition-all ${
                  active
                    ? "border-brand bg-brand/[0.08]"
                    : "border-border hover:border-ink"
                } disabled:opacity-50`}
              >
                <span className="font-display font-bold text-sm">{opt.label}</span>
                <span className="mono text-[10px] text-ink-muted">{opt.tag}</span>
                {active && <span className="mono text-[9px] tracking-wider text-brand font-semibold">ACTIVE</span>}
                {switching === opt.plan && <span className="mono text-[9px] text-ink-muted">switching…</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="surface-card hard-shadow-sm p-6 lg:p-8 max-w-2xl mb-8" data-testid="stripe-settings-card">
        <div className="flex items-center gap-2 mb-2">
          <Key size={20} className="text-brand" weight="duotone" />
          <div className="label-overline">Stripe API key</div>
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Payment provider</h2>
        <p className="text-sm text-ink-muted mb-6">
          Your Stripe <span className="mono">sk_live_…</span> (or <span className="mono">sk_test_…</span>) secret key.
          Stored encrypted in the database. Falls back to the <span className="mono">STRIPE_API_KEY</span> env var if unset.
        </p>

        <div className="border border-border rounded-sm p-4 bg-secondary mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-overline mb-1">Current status</div>
              <div className="text-sm">
                {settings.stripe_api_key_set ? (
                  <>
                    <span className="text-success font-semibold">Configured</span>
                    <span className="text-ink-muted"> · from {settings.stripe_api_key_source}</span>
                    {settings.stripe_api_key_preview && (
                      <span className="text-ink-muted mono"> · ends with …{settings.stripe_api_key_preview}</span>
                    )}
                  </>
                ) : (
                  <span className="text-critical font-semibold">Not configured — billing disabled</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={save} data-testid="stripe-settings-form" className="space-y-4">
          <label className="block">
            <div className="label-overline mb-1.5">New Stripe secret key</div>
            <div className="relative">
              <input
                type={show ? "text" : "password"}
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="sk_live_..."
                data-testid="stripe-key-input"
                className="w-full bg-white border border-ink rounded-sm px-4 py-3 pr-12 outline-none focus:ring-2 focus:ring-brand mono text-sm"
              />
              <button type="button" onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink"
                data-testid="stripe-key-toggle">
                {show ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </label>
          <button
            type="submit"
            disabled={saving || !key.trim()}
            data-testid="stripe-key-save"
            className="bg-ink text-white px-5 py-2.5 rounded-sm font-semibold hover:bg-brand transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save key"}
          </button>
        </form>
      </div>

      <div className="surface-card p-6 max-w-2xl bg-secondary border-dashed text-sm text-ink-muted leading-relaxed">
        <div className="label-overline text-ink mb-2">How to get your Stripe key</div>
        <ol className="list-decimal list-inside space-y-1">
          <li>Log in at <a className="text-brand hover:underline" href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer">dashboard.stripe.com/apikeys</a>.</li>
          <li>Copy the <strong>Secret key</strong> (<span className="mono">sk_live_…</span> for production, <span className="mono">sk_test_…</span> for testing).</li>
          <li>Paste it above and click <strong>Save</strong>. Revenue flows directly to your account — Auditor never holds funds.</li>
        </ol>
      </div>

      {/* SMTP — admin-only, never exposed to other users. Powers
          scheduled-audit emails + team invites. */}
      <div className="surface-card hard-shadow-sm p-6 lg:p-8 max-w-2xl mt-10 mb-8" data-testid="smtp-settings-card">
        <div className="flex items-center gap-2 mb-2">
          <Envelope size={20} className="text-brand" weight="duotone" />
          <div className="label-overline">Outbound email (SMTP)</div>
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight mb-1">cPanel SMTP server</h2>
        <p className="text-sm text-ink-muted mb-6">
          Used for scheduled-audit diff emails and Agency-workspace invites.
          Stored in the database and visible only to admins. Use your cPanel
          mail account — see the help block at the bottom of this page.
        </p>

        <div className="border border-border rounded-sm p-4 bg-secondary mb-6 text-sm">
          <div className="label-overline mb-1">Current status</div>
          {settings.smtp_host && settings.smtp_password_set ? (
            <div>
              <span className="text-success font-semibold">Configured</span>
              <span className="text-ink-muted mono"> · {settings.smtp_host}:{settings.smtp_port}</span>
              <span className="text-ink-muted"> · from {settings.smtp_from_email}</span>
            </div>
          ) : (
            <span className="text-warning font-semibold">Not configured — emails will fail</span>
          )}
        </div>

        <form onSubmit={saveSmtp} data-testid="smtp-settings-form" className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <div className="label-overline mb-1.5">SMTP host</div>
              <input
                type="text" value={smtp.smtp_host}
                onChange={(e) => setSmtp({ ...smtp, smtp_host: e.target.value })}
                placeholder="mail.yourdomain.com"
                data-testid="smtp-host-input"
                className="w-full bg-white border border-ink rounded-sm px-4 py-3 outline-none focus:ring-2 focus:ring-brand mono text-sm"
              />
            </label>
            <label className="block">
              <div className="label-overline mb-1.5">Port</div>
              <input
                type="text" value={smtp.smtp_port}
                onChange={(e) => setSmtp({ ...smtp, smtp_port: e.target.value })}
                placeholder="587 (STARTTLS) or 465 (SSL)"
                data-testid="smtp-port-input"
                className="w-full bg-white border border-ink rounded-sm px-4 py-3 outline-none focus:ring-2 focus:ring-brand mono text-sm"
              />
            </label>
          </div>
          <label className="block">
            <div className="label-overline mb-1.5">Username</div>
            <input
              type="text" value={smtp.smtp_username}
              onChange={(e) => setSmtp({ ...smtp, smtp_username: e.target.value })}
              placeholder="auditor@yourdomain.com"
              data-testid="smtp-username-input"
              className="w-full bg-white border border-ink rounded-sm px-4 py-3 outline-none focus:ring-2 focus:ring-brand mono text-sm"
            />
          </label>
          <label className="block">
            <div className="label-overline mb-1.5">Password {settings.smtp_password_set && <span className="text-ink-muted font-normal normal-case tracking-normal text-[10px]"> · already saved · leave blank to keep</span>}</div>
            <div className="relative">
              <input
                type={smtpShow ? "text" : "password"} value={smtp.smtp_password}
                onChange={(e) => setSmtp({ ...smtp, smtp_password: e.target.value })}
                placeholder={settings.smtp_password_set ? "••••••••" : "Your SMTP password"}
                data-testid="smtp-password-input"
                className="w-full bg-white border border-ink rounded-sm px-4 py-3 pr-12 outline-none focus:ring-2 focus:ring-brand mono text-sm"
                autoComplete="new-password"
              />
              <button type="button" onClick={() => setSmtpShow(!smtpShow)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink"
                data-testid="smtp-password-toggle">
                {smtpShow ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </label>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <div className="label-overline mb-1.5">From email</div>
              <input
                type="email" value={smtp.smtp_from_email}
                onChange={(e) => setSmtp({ ...smtp, smtp_from_email: e.target.value })}
                placeholder="no-reply@yourdomain.com"
                data-testid="smtp-from-email-input"
                className="w-full bg-white border border-ink rounded-sm px-4 py-3 outline-none focus:ring-2 focus:ring-brand mono text-sm"
              />
            </label>
            <label className="block">
              <div className="label-overline mb-1.5">From name</div>
              <input
                type="text" value={smtp.smtp_from_name}
                onChange={(e) => setSmtp({ ...smtp, smtp_from_name: e.target.value })}
                placeholder="Auditor"
                data-testid="smtp-from-name-input"
                className="w-full bg-white border border-ink rounded-sm px-4 py-3 outline-none focus:ring-2 focus:ring-brand text-sm"
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox"
              checked={smtp.smtp_use_tls === "true"}
              onChange={(e) => setSmtp({ ...smtp, smtp_use_tls: e.target.checked ? "true" : "false" })}
              data-testid="smtp-tls-toggle"
            />
            <span>Use STARTTLS (recommended for port 587)</span>
          </label>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={smtpSaving}
              data-testid="smtp-save-btn"
              className="bg-ink text-white px-5 py-2.5 rounded-sm font-semibold hover:bg-brand transition-colors disabled:opacity-50"
            >
              {smtpSaving ? "Saving…" : "Save SMTP settings"}
            </button>
            <div className="flex items-center gap-2">
              <input
                type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@your-inbox.com"
                data-testid="smtp-test-email-input"
                className="bg-white border border-border rounded-sm px-3 py-2 outline-none focus:ring-2 focus:ring-brand mono text-xs w-56"
              />
              <button
                type="button" onClick={sendTest} disabled={testing || !settings.smtp_password_set}
                data-testid="smtp-test-send-btn"
                className="inline-flex items-center gap-1.5 border border-ink text-ink hover:bg-ink hover:text-white transition-colors px-3 py-2 rounded-sm font-semibold text-xs disabled:opacity-50"
                title={!settings.smtp_password_set ? "Save SMTP settings first" : "Send a test email to verify"}
              >
                <PaperPlaneTilt size={14} />
                {testing ? "Sending…" : "Send test"}
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="surface-card p-6 max-w-2xl bg-secondary border-dashed text-sm text-ink-muted leading-relaxed">
        <div className="label-overline text-ink mb-2">cPanel SMTP — where to find these values</div>
        <ol className="list-decimal list-inside space-y-2">
          <li>cPanel → <strong>Email Accounts</strong> → either create a dedicated mailbox (e.g. <span className="mono">auditor@yourdomain.com</span>) or pick an existing one.</li>
          <li>Next to the account, click <strong>Connect Devices</strong>. You&apos;ll see <strong>Secure SSL/TLS Settings</strong>.</li>
          <li>
            Copy these values into the form above:
            <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5">
              <li><strong>SMTP host</strong> → <span className="mono">mail.yourdomain.com</span> (usually)</li>
              <li><strong>Port</strong> → <span className="mono">587</span> (STARTTLS) or <span className="mono">465</span> (SSL)</li>
              <li><strong>Username</strong> → the full email address</li>
              <li><strong>Password</strong> → the mailbox password</li>
              <li><strong>From email</strong> → same as username</li>
            </ul>
          </li>
          <li>Click <strong>Save SMTP settings</strong>, then enter your own inbox in the test field and click <strong>Send test</strong> to confirm delivery.</li>
        </ol>
      </div>
    </AppShell>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="surface-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className="label-overline">{label}</div>
        <Icon size={18} className="text-ink-muted" />
      </div>
      <div className="num-display text-4xl">{value}</div>
    </div>
  );
}
